import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useBrand } from "@/contexts/brand-context";
import { useSubscription } from "@/contexts/subscription-context";
import { pb } from "@/lib/pocketbase";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import {
  generateListicleItems,
  generateListicleArticle,
  type ListicleItem,
} from "@/services/articleGeneration";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { toast } from "sonner";
import { ListicleItemsList } from "./ListicleItemsList";

// Custom spinner component
const Spinner = ({ size = "lg" }: { size?: string }) => (
  <div
    className={`animate-spin rounded-full border-2 border-current border-t-transparent ${
      size === "lg" ? "h-8 w-8" : "h-4 w-4"
    }`}
  />
);

// Using types from service layer

interface ListicleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onArticleStarted: (postId: string) => void;
}

export default function ListicleModal({
  isOpen,
  onClose,
  onArticleStarted,
}: ListicleModalProps) {
  const { currentBrand } = useBrand();
  const { hasAvailableQuota } = useSubscription();
  const [topic, setTopic] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [items, setItems] = useState<ListicleItem[]>([]);
  const [step, setStep] = useState<"topic" | "items">("topic");

  // Handle topic input and generate items
  const handleGenerateItems = async () => {
    if (!topic.trim()) {
      toast.error("Please enter a topic");
      return;
    }

    if (!currentBrand) {
      toast.error("Please select a brand first");
      return;
    }

    if (!hasAvailableQuota()) {
      toast.error("You've reached your monthly article limit. Please upgrade your plan for more articles.");
      return;
    }

    setLoading(true);
    setLoadingMessage("Generating list items...");

    try {
      const data = await generateListicleItems({
        topic: topic.trim(),
        user_id: pb.authStore.model?.id || "",
      });

      if (!data.success || !data.data) {
        throw new Error(data.error || "Failed to generate items");
      }

      setItems(data.data.items);
      setStep("items");
      toast.success(`Generated ${data.data.items.length} items for "${topic}"`);
    } catch (error) {
      console.error("Error generating items:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to generate items"
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle article generation with selected items
  const handleGenerateArticle = async (selectedItems: ListicleItem[]) => {
    if (!currentBrand) {
      toast.error("Please select a brand first");
      return;
    }

    if (selectedItems.length < 3) {
      toast.error("Please select at least 3 items");
      return;
    }

    setLoading(true);
    setLoadingMessage("Starting article generation...");

    try {
      const data = await generateListicleArticle({
        topic: topic.trim(),
        items: selectedItems.map(item => ({
          name: item.name,
          website: item.website,
        })),
        user_id: pb.authStore.model?.id || "",
        brand_id: currentBrand.id,
      });

      if (!data.success || !data.data?.postId) {
        throw new Error(data.error || "Failed to start article generation");
      }

      // Close modal and start progress tracking
      handleClose();
      toast.success("Listicle generation started!");
      
      // Start progress tracking with the returned postId
      onArticleStarted(data.data.postId);
    } catch (error) {
      console.error("Error generating article:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to generate article"
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    setTopic("");
    setItems([]);
    setStep("topic");
    setLoading(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Create Listicle Article</DialogTitle>
          <DialogDescription>
            {step === "topic"
              ? "Enter a topic to generate a list of items for your listicle."
              : `Review and select items for your "${topic}" listicle.`}
          </DialogDescription>
        </DialogHeader>

        {step === "topic" ? (
          // Step 1: Topic Input
          <div className="space-y-4">
            <div>
              <Label htmlFor="listicle-topic" className="font-medium">
                Topic <span className="text-red-500">*</span>
              </Label>
              <Input
                id="listicle-topic"
                placeholder="e.g., project management tools, email marketing platforms, CRM software"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                className="mt-2"
                disabled={loading}
              />
              <p className="text-sm text-muted-foreground mt-1">
                Describe what you want to create a list about. Be specific for better results.
              </p>
            </div>

            {loading && (
              <div className="flex justify-center items-center py-8">
                <Spinner size="lg" />
                <span className="ml-2">{loadingMessage}</span>
              </div>
            )}
          </div>
        ) : (
          // Step 2: Items Review
          <ListicleItemsList
            items={items}
            onGenerateArticle={handleGenerateArticle}
            loading={loading}
            loadingMessage={loadingMessage}
          />
        )}

        <DialogFooter className="flex justify-between items-center">
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          
          {step === "topic" ? (
            <Button
              onClick={handleGenerateItems}
              disabled={!topic.trim() || loading}
              className="bg-black hover:bg-black/90"
            >
              {loading ? (
                <>
                  <Spinner size="sm" />
                  <span className="ml-2">Generating...</span>
                </>
              ) : (
                <>
                  Generate Items
                  <FontAwesomeIcon icon={faArrowRight} className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={() => {
                setStep("topic");
                setItems([]);
              }}
              disabled={loading}
            >
              Back to Topic
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}